import type { PaginatedRequest, PaginatedResponse } from '@/types';
import type { Commerce } from '../models';
import type { ServiceWithOperator } from '../../LogisticOperator/models/ServiceWithOperator';
import logisticOperatorAssociationService from './logistic-operator-association.service';

export type ServiceWithOperatorCommerceService = ServiceWithOperator & {
  name?: string;
  commercialName?: string;
  logisticOperator?: {
    id: string;
    name?: string;
    businessName?: string;
  } | null;
};

export interface ServiceWithOperatorCommerce {
  id: string;
  serviceWithOperatorId: string;
  commerceId: string;
  serviceWithOperator?: ServiceWithOperatorCommerceService | null;
  commerce?: Commerce | null;
}

export interface ServiceWithOperatorCommerceFilters {
  commerceId?: string;
  serviceWithOperatorId?: string;
}

export interface CreateServiceWithOperatorCommercePayload {
  serviceWithOperatorId: string;
  commerceId: string;
}

const serviceWithOperatorCommercesService =
  logisticOperatorAssociationService.injectEndpoints({
  endpoints: (builder) => ({
    getServiceWithOperatorCommerceById: builder.query<
      ServiceWithOperatorCommerce,
      { id: string }
    >({
      query: ({ id }) => ({ url: `/service-with-operator-commerces/${id}` }),
      providesTags: ['LogisticOperatorAssociation'],
    }),
    getServiceWithOperatorCommerces: builder.query<
      PaginatedResponse<ServiceWithOperatorCommerce>,
      {
        paginated: PaginatedRequest;
        filters?: ServiceWithOperatorCommerceFilters;
      }
    >({
      query: ({ paginated, filters }) => ({
        url: '/service-with-operator-commerces',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters?.commerceId && { commerceId: filters.commerceId }),
          ...(filters?.serviceWithOperatorId && {
            serviceWithOperatorId: filters.serviceWithOperatorId,
          }),
        },
      }),
      providesTags: ['LogisticOperatorAssociation'],
    }),
    getServiceWithOperatorCommercesByCommerce: builder.query<
      ServiceWithOperatorCommerce[],
      { commerceId: string }
    >({
      query: ({ commerceId }) => ({
        url: `/service-with-operator-commerces/commerce/${commerceId}`,
      }),
      providesTags: ['LogisticOperatorAssociation'],
    }),
    getServiceWithOperatorCommercesByServiceWithOperator: builder.query<
      ServiceWithOperatorCommerce[],
      { serviceWithOperatorId: string }
    >({
      query: ({ serviceWithOperatorId }) => ({
        url: `/service-with-operator-commerces/service-with-operator/${serviceWithOperatorId}`,
      }),
      providesTags: ['LogisticOperatorAssociation'],
    }),
    createServiceWithOperatorCommerce: builder.mutation<
      ServiceWithOperatorCommerce,
      CreateServiceWithOperatorCommercePayload
    >({
      query: (body) => ({
        url: '/service-with-operator-commerces',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogisticOperatorAssociation'],
    }),
    deleteServiceWithOperatorCommerce: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/service-with-operator-commerces/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LogisticOperatorAssociation'],
    }),
  }),
});

export default serviceWithOperatorCommercesService;
export const {
  useGetServiceWithOperatorCommerceByIdQuery,
  useGetServiceWithOperatorCommercesQuery,
  useGetServiceWithOperatorCommercesByCommerceQuery,
  useGetServiceWithOperatorCommercesByServiceWithOperatorQuery,
  useCreateServiceWithOperatorCommerceMutation,
  useDeleteServiceWithOperatorCommerceMutation,
} = serviceWithOperatorCommercesService;
